import styled from "styled-components";
import ModalHeader from "../../../../Components/ModalHeader/ModalHeader";
import * as MdIcons from 'react-icons/md';
import { ModalFooter, FieldLabel, FieldHint, FieldError } from "../../../../Constants/StyledComponents";
import { useEffect, useState, useRef, useCallback } from "react";
import { useModalProvider } from "../../../../Providers/ModalProvider";
import EditAnnotationModalities from "./EditAnnotationModalities";
import { useAnnotationsProvider } from "../../../../Providers/AnnotationsProvider";
import CancelEditAnnotationModal from "./CancelEditAnnotationModal";
import CircularCheckboxComponent from "../../../../Components/CircularCheckbox/CircularCheckbox";
import { selectedAnnotationAtom } from "../../../../Pages/Data/Visualize/DataReview/Atoms/Annotations";
import { useRecoilValue } from "recoil";
import ModalFooterButton from "../../../../Components/ModalFooterButton/ModalFooterButton";
import { currentPatientFileInfoAtom } from "../../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import { useAnnotationService } from "../../../../Hooks/useAnnotationService";
import { useAnnotationShortcutService } from "../../../../Hooks/useAnnotationShortcutService";
import MultivalueTextField from "../../../../Components/TextField/MultivalueTextField";
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { updateHotkeyTrigger } from "../../../../Pages/Data/Visualize/DataReview/Atoms/Hotkeys";
import { TraceColorPicker } from "../Modals/LineGraphModalities/TraceColorPicker";
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergIconSize, MobergTheme } from "../../../../Moberg";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from "../../../../Components/ConfirmationModal/ConfirmationModal"
import { MobergFontFamily } from '../../../../Components/MobergFont/MobergFont';
import { useDataReviewPermissions } from "../../../../Hooks/useDataReviewPermissions";
import { useAsyncTask } from "../../../../Hooks/useAsyncTask";
import { formatDate } from "../../../../Pages/Data/Visualize/DataReview/utils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import tz from "dayjs/plugin/timezone"

dayjs.extend(utc)
dayjs.extend(tz)

export function EditAnnotation(props) {
    const { canManageDataReview } = useDataReviewPermissions()
    const annotationsProvider = useAnnotationsProvider()

    const { close, createModal } = useModalProvider()

    const selectedAnnotationAtomAnnotation = useRecoilValue(selectedAnnotationAtom)

    // if the EditAnnotation modal is opened up from HotkeysModal.js, then the configs should come from props rather than the selectedAnnotationAtom
    const selectedAnnotation = props.mode === "CREATE" ? props.config : props.mode === "EDIT" ? { ...props.config.data, id: props.config.id } : selectedAnnotationAtomAnnotation
    const selectedAnnotationIsReadOnly = !props.isHotkeysModal && !!selectedAnnotation?.read_only
    const canModifyAnnotation = props.isHotkeysModal || (canManageDataReview && !selectedAnnotationIsReadOnly)

    // start datetime and end datetime for the entire patient file. these are not annotation start/end times
    const { fileStartDate, fileEndDate, timeZone } = useRecoilValue(currentPatientFileInfoAtom)

    const dateFormatOpts = { timeZone, timeZoneName: true, seconds: true }

    const { modifyAnnotation, deleteAnnotations } = useAnnotationService()
    const { deleteAnnotationShortcuts } = useAnnotationShortcutService()

    // handles discarding unsaved changes when pressing 'Cancel' button. never call __setUneditedAnnotationConfigs directly because of shallow copy. instead, use setUneditedAnnotationConfigs callback
    const [uneditedAnnotationConfigs, __setUneditedAnnotationConfigs] = useState(structuredClone(selectedAnnotation))

    // never call __setUneditedAnnotationConfigs directly because of shallow copy. use this callback to handle setting a new value for uneditedAnnotationConfigs
    const setUneditedAnnotationConfigs = useCallback((s) => {
        __setUneditedAnnotationConfigs(structuredClone(s))
    }, [__setUneditedAnnotationConfigs])

    //useState handles storing annotator name as a string from get_annotations() endpoint
    const [annotatorName, setAnnotatorName] = useState(selectedAnnotation.user_full_name)

    //useState handles storing annotator email as a string from get_annotations() endpoint
    const [annotatorEmail, setAnnotatorEmail] = useState(selectedAnnotation.user_email)

    //useState handles storing annotation creation timestamp as a UTC timestamp from get_annotations() endpoint
    const [annotationTimestamp, setAnnotationTimestamp] = useState(selectedAnnotation.timestamp == null ? "" : formatDate(new Date(selectedAnnotation.timestamp), dateFormatOpts))

    // useState handles storing annotation description as a string
    const [annotationDescription, setAnnotationDescription] = useState(selectedAnnotation.description)

    // useState handles storing annotation title as a string
    const [annotationTitle, setAnnotationTitle] = useState(selectedAnnotation.text)

    // useState handles holding annotation tags as an array of strings. ex.) ['tag1', 'tag2']
    const [annotationTags, setAnnotationTags] = useState(selectedAnnotation.tags)

    // useState stores error message as a string when validating tags
    const [tagError, setTagError] = useState('')

    const tagInputRef = useRef(null);
    const tagsContainerRef = useRef(null)
    const annotationTitleInputRef = useRef(null)

    // useState renders input box when user adds a new tag (Boolean; true = render input box to add a new tag)
    const [isAddTag, setIsAddTag] = useState(false)

    // useState stores input/value as a string when adding a new tag
    const [newTagValue, setNewTagValue] = useState('')

    // boolean useState becomes true when there are unsaved edits/changes
    const [unsavedEdits, setUnsavedEdits] = useState(false)

    // handles storing the original value of annotationsProvider.isPointAnnotation as a Boolean to handle enabling/disabling the Save button when switching between span and point annotations
    const [intervalStatus, setIntervalStatus] = useState(annotationsProvider.isPointAnnotation)

    const [unsavedHotkeyConfig,] = useState(structuredClone(props.config))
    const [hotkeyConfig, setHotkeyConfig] = useState(structuredClone(props.config))
    const hotkeyEditDisabled = (props.isHotkeysModal && JSON.stringify(hotkeyConfig?.triggers) === '[null,null]') || hotkeyConfig?.editModeWarning || hotkeyConfig?.isInEditMode

    /*
     * A React useEffect hook that handles setting values of annotationsProvider.isPointAnnotation based on original, unedited annotation start and end times
     * This useEffect only runs on the first render
    */
    useEffect(() => {
        // only check start and end times when editing an annotation, not a keyboard shortcut/hotkey
        if (!props.isHotkeysModal) {
            // In point annotations, start and end times are exactly the same. If not, the annotation is a span annotation
            if (uneditedAnnotationConfigs.start_time === uneditedAnnotationConfigs.end_time) {
                annotationsProvider.setIsPointAnnotation(true)
                setIntervalStatus(true)

            } else {
                annotationsProvider.setIsPointAnnotation(false)
                setIntervalStatus(false)
            }
        } else {
            // Keyboard shortcuts/hotkeys do not have an annotation timestamp, annotator name, or annotator email
            setAnnotationTimestamp()
            setAnnotatorName("")
            setAnnotatorEmail("")
            if (props.mode === "CREATE" && !props.isHotkeysModal) {
                annotationTitleInputRef.current.focus()
            }
        }
    }, [])

    // handles color value for annotation configs (hexcode form)
    const [annotationColor, setAnnotationColor] = useState(selectedAnnotation.color)

    // useState stores opacity value for annotation configs as a float (ex. 0.2 opacity = 20% opacity)
    const [annotationOpacity, setAnnotationOpacity] = useState(0.2)

    /**
    * Handles the logic for updating the color state after receiving the updated color object when the color selection changes
    *
    * @param {Color Object} nextState - The updated color object when color selection changes in the ColorPicker
    */
    const handleColorChange = (color) => {
        if (!canModifyAnnotation) {
            return
        }

        setAnnotationColor(color)
        setUnsavedEdits(true)
    }

    // useState contains the start datetime of the selected annotation as an integer
    const [startDate, setStartDate] = useState(selectedAnnotation.start_time)

    // useState contains the end datetime of the selected annotation as an integer
    const [endDate, setEndDate] = useState(selectedAnnotation.end_time ? selectedAnnotation.end_time : selectedAnnotation.start_time) // if end_time doesn't exist, this means the annotation is a Point Annotation. In this case, end_time = start_time

    /*
     * A React useEffect hook that detects click events and handles focusing/blurring the new tag input box. This useEffect also handles behavior when clicking outside the tags container to exit the workflow
    */
    useEffect(() => {
        // Function to handle clicks outside of the target div
        const handleOutsideClick = (event) => {
            if ((tagsContainerRef.current && !tagsContainerRef.current.contains(event.target)) || (event.target.id !== "tagsContainerDiv" && event.target.id !== "addMoreTagsButton")) {
                if (newTagValue.length > 0) {
                    if ((!annotationTags.includes(newTagValue.trim())) && newTagValue.trim() !== '') {
                        setAnnotationTags(prevTags => [
                            ...prevTags,
                            newTagValue
                        ])
                    }
                    setNewTagValue('')
                    tagInputRef.current.blur()
                }
                setIsAddTag(false)
            } else if (event.target.id === "tagsContainerDiv" || event.target.id === "addMoreTagsButton") {
                // Click occurred inside the target div
                if (isAddTag) {
                    tagInputRef.current.focus()
                }
            }
        }

        // Attach the click event listener to the document
        document.addEventListener('click', handleOutsideClick);

        // Clean up the event listener when the component unmounts
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        }
    })


    /**
    * Handles the logic for removing/deleting a tag by pressing it's corresponding 'X' button
    *
    * @param {String} tagName - The name of the selected tag
    */
    function removeTag(tagName) {
        if (!canModifyAnnotation) {
            return
        }

        setAnnotationTags(prevState => prevState.filter(tag => tag !== tagName))
        setTagError("")
    }

    function addTag(value, setChips) {
        if (!canModifyAnnotation) {
            return
        }

        // tagExists is a Boolean. It is true if the annotationTags array already includes the new tag value
        const tagExists = annotationTags.includes(value)
        // Pressing 'Enter' begins the validation process for adding a tag
        if (value.trim() === '') { // Checks if the new tag is blank or whitespace
            setTagError("")
            return
        } else if (value.length > 25) {
            setTagError('Tags cannot be longer than 25 characters.')
        } else if (tagExists) { // Checks if the new tag already exists
            setTagError('Tag already exists.')
        } else { // If new tag is valid, continue adding the tag to the annotationTags array
            setAnnotationTags(prevTags => [
                ...prevTags,
                value
            ])
            setChips(prev => [...prev, value])
            setTagError('')
        }
    }


    /**
    * Handles functionality for any key press actions when modifying the annotation title
    *
    * @param {onKeyDown Event} event - The key pressed while typing in a new annotation title value
    */
    const saveNewAnnotationTitle = (event) => {
        if (!canModifyAnnotation) {
            return
        }

        if (event.key === 'Enter') { // Pressing 'Enter' saves the new annotation title
            event.target.blur()
        }
    }

    /*
     * A React useEffect hook that handles disabling/enabling for Save button (unsavedEdits useState).
     * If the EditAnnotation modal is opened up from HotkeysModal.js, it is unnecessary to check for changes in start_time, end_time, and interval status
    */
    useEffect(() => {
        const annotationModalities = JSON.stringify(annotationsProvider.annotationModalities.slice().sort())
        const uneditedAnnotationModalities = JSON.stringify(annotationsProvider.uneditedModalitiesProvider.slice().sort())
        const currentAnnotationTags = annotationTags.slice().sort().join('')
        const uneditedAnnotationTags = uneditedAnnotationConfigs.tags.slice().sort().join('')

        if (annotationModalities !== uneditedAnnotationModalities) { // checks for changes in selected annotation's modalities
            setUnsavedEdits(true)
            annotationsProvider.setUnsavedModalityEdits(true)
        } else if (annotationTitle !== uneditedAnnotationConfigs.text) { // checks for changes in selected annotation's title
            setUnsavedEdits(true)
            annotationsProvider.setUnsavedModalityEdits(false)
        } else if (annotationDescription !== uneditedAnnotationConfigs.description) { // checks for changes in selected annotation's description
            if (annotationDescription.length > 500) {
                setUnsavedEdits(false)

            } else {
                setUnsavedEdits(true)
            }
            annotationsProvider.setUnsavedModalityEdits(false)
        } else if (annotationColor !== uneditedAnnotationConfigs.color) {
            setUnsavedEdits(true)
            annotationsProvider.setUnsavedModalityEdits(false)
        } else if (currentAnnotationTags !== uneditedAnnotationTags) { // checks for changes in selected annotation's tags
            if (String(tagError).length > 0 || annotationTags.length > 10) {
                setUnsavedEdits(false)
                annotationsProvider.setUnsavedModalityEdits(false)
            } else {
                setUnsavedEdits(true)
                annotationsProvider.setUnsavedModalityEdits(false)
            }
        } else if (startDate && (parseInt(startDate) !== parseInt(uneditedAnnotationConfigs.start_time)) && !props.isHotkeysModal) { // checks if the selected annotation is a keyboard shortcut/hotkey and if not, checks for changes in selected annotation's start datetime
            setUnsavedEdits(true)
            annotationsProvider.setUnsavedModalityEdits(false)
        } else if (endDate && (parseInt(endDate) !== parseInt(uneditedAnnotationConfigs.end_time)) && !props.isHotkeysModal) { // checks if the selected annotation is a keyboard shortcut/hotkey and if not, checks for changes in selected annotation's end datetime
            setUnsavedEdits(true)
            annotationsProvider.setUnsavedModalityEdits(false)
        } else if (annotationsProvider.isPointAnnotation !== intervalStatus && (!props.isHotkeysModal)) { // checks if the selected annotation is a keyboard shortcut/hotkey and if not, checks for changes in selected annotation's interval status (whether it's a point or span annotation)
            setUnsavedEdits(true)
            annotationsProvider.setUnsavedModalityEdits(false)
        } else if (props.isHotkeysModal && JSON.stringify(unsavedHotkeyConfig?.triggers) !== JSON.stringify(hotkeyConfig?.triggers)) {
            setUnsavedEdits(true)
        } else { // no changes have been made, so disable the 'Save' button
            setUnsavedEdits(false)
            annotationsProvider.setUnsavedModalityEdits(false)
        }
    }, [annotationTitle, annotationDescription, annotationTags, annotationsProvider.annotationModalities, startDate, endDate, annotationsProvider.isPointAnnotation, props.isHotkeysModal, annotationColor, hotkeyConfig, unsavedHotkeyConfig])

    /**
    * Handles opening up the delete annotation confirmation modal
    */
    const renderDeleteAnnotationModal = () => {
        if (!canModifyAnnotation) {
            return
        }

        createModal(
			<ConfirmationModal
				title={"Delete annotation?"}
				description={`Are you sure you want to permanently delete this annotation? This action cannot be undone.`}
				confirmButton={{
					text: "Yes, delete annotation",
					theme: MobergTheme.RED,
					// On success, close() closes this confirmation and ConfirmationModal then closes the Edit modal.
					// On failure, skip it: ConfirmationModal closes only itself and the error shows in the footer.
					onClick: () => handleDeleteAnnotation().then(deleted => deleted && close())
				}}
			/>
		)
    }

    /**
    * Handles opening up the save confirmation modal (only for EDIT mode, not CREATE)
    */
    const handleSaveWithConfirmation = () => {
        if (!canModifyAnnotation) {
            return
        }

        // Skip confirmation when creating a new shortcut
        if (props.mode === "CREATE") {
            handleSaveAnnotation()
            return
        }

        const itemType = props.isHotkeysModal ? "shortcut" : "annotation"
        createModal(
            <ConfirmationModal
                title={`Save ${itemType}?`}
                description={`Are you sure you want to save changes to this ${itemType}?`}
                confirmButton={{
                    text: `Yes, save ${itemType}`,
                    theme: MobergTheme.BLUE,
                    onClick: handleSaveAnnotation
                }}
            />
        )
    }

    /**
    * Handles deleting the selectedAnnotation. Returns true when deleted; on failure the error shows in the footer.
    */
    async function handleDeleteAnnotation() {
        if (!canModifyAnnotation) {
            return false
        }

        if (props.isHotkeysModal) {
            // Delete keyboard shortcut using the service
            if (!await runWithFooterError(deleteShortcutTask, "Could not delete shortcut. Please try again.", props.config.id).then(() => true, () => false)) {
                return false
            }
        } else {
            // Delete regular annotation
            if (!await runWithFooterError(deleteAnnotationTask, "Could not delete annotation. Please try again.", selectedAnnotation.id).then(() => true, () => false)) {
                return false
            }
        }
        annotationsProvider.resetAnnotationsProvider()
        return true
    }

    async function handleCloseAndSaveAnnotation() {
        if (!canModifyAnnotation) {
            await close()
            return
        }

        await handleSaveAnnotation()
        await close()
    }

    /**
    * Handles opening up the cancel changes confirmation modal if there are unsaved changes.
    * If there are no unsaved changes, just close the modal.
    */
    async function renderCancelConfirmationModal() {
        if (!canModifyAnnotation) {
            await close()
            annotationsProvider.resetAnnotationsProvider()
            return
        }

        if (unsavedEdits || annotationsProvider.unsavedModalityEdits || (annotationTags.slice().sort().join('') !== uneditedAnnotationConfigs.tags.slice().sort().join(''))) {
            createModal(<CancelEditAnnotationModal
                escClose={false}
                clickOutClose={false}
                disableSave={((annotationTags.slice().sort().join('') !== uneditedAnnotationConfigs.tags.slice().sort().join('')) && annotationTags.length > 10) || annotationDescription.length > 500}
                cancelAction={() => { cancelUnsavedChanges() }}
                saveAction={() => { handleCloseAndSaveAnnotation() }}

            />)
        } else {
            setUnsavedEdits(false)
            annotationsProvider.setUnsavedModalityEdits(false)
            await close()
        }
    }

    /**
    * Handles reverting all annotation configs to the last saved state, resetting the AnnotationsProvider, and closing the Cancel Confirmation and Edit Annotation modals
    */
    async function cancelUnsavedChanges() {
        setAnnotationTitle(uneditedAnnotationConfigs.text)
        setAnnotationDescription(uneditedAnnotationConfigs.description)
        setAnnotationTags(uneditedAnnotationConfigs.tags)
        setAnnotationColor(uneditedAnnotationConfigs.color)
        setAnnotationOpacity(0.2)
        setStartDate(uneditedAnnotationConfigs.start_time)
        setEndDate(uneditedAnnotationConfigs.end_time)
        setUnsavedEdits(false)
        // First close will close the confirmation modal. The second closes the main modal.
        await close()
        await close()
        annotationsProvider.resetAnnotationsProvider()
    }

    // Errors due to invalid annotation intervals
    const [startIntervalError, setStartIntervalError] = useState(null)
    const [endIntervalError, setEndIntervalError] = useState(null)
    const [saveError, setSaveError] = useState("")
    const saveShortcutTask = useAsyncTask((_signal, data) => props.onSave(data))
    const saveAnnotationTask = useAsyncTask((_signal, data) => modifyAnnotation(selectedAnnotation, data))
    const deleteShortcutTask = useAsyncTask((_signal, id) => deleteAnnotationShortcuts([id]))
    const deleteAnnotationTask = useAsyncTask((_signal, id) => deleteAnnotations([id]))
    const footerErrorStyle = { alignSelf: "center", marginTop: 0, marginRight: "12px" }
    // Only the most recent save/delete failure shows in the footer.
    const [footerError, setFooterError] = useState("")

    const runWithFooterError = (task, errorMessage, ...args) => {
        setFooterError("")
        return task.run(...args).catch(error => {
            setFooterError(errorMessage)
            throw error
        })
    }

    const describeIntervalError = (error, field) => {
      switch(error) {
        case 'maxTime':
        case 'maxDate':
          if (field === 'start') {
            return annotationsProvider.isPointAnnotation
              ? "Must be before the file end time"
              : "Must be before the end time"
          }
          return "Must be before the file end time"

        case 'minTime':
        case 'minDate':
          return field === 'start'
            ? "Must be after the file start time"
            : "Must be after the start time"

        case 'invalidDate':
          return "The specified time is invalid"

        default:
          return ""
      }
    }

    const startErrorMessage = describeIntervalError(startIntervalError, 'start')
    const endErrorMessage = annotationsProvider.isPointAnnotation ? "" : describeIntervalError(endIntervalError, 'end')

    /**
    * Handles saving changes to an annotation configuration, saving changes to a keyboard shortcut's configurations, or saving changes after creating a new keyboard shortcut
    */
    async function handleSaveAnnotation() {
        if (!canModifyAnnotation) {
            return
        }

        if (!props.isHotkeysModal && !annotationsProvider.isPointAnnotation) {
            if (startDate > endDate) {
                setSaveError("Start time must be before end time")
                return
            } else if (startDate === endDate) {
                setSaveError("Start and end times cannot be the same for a range annotation")
                return
            }
        }

        const data = {
            id: selectedAnnotation.id,
            start_time: startDate,
            end_time: annotationsProvider.isPointAnnotation ? startDate : endDate,
            text: annotationTitle,
            description: annotationDescription,
            type: selectedAnnotation.type,
            color: annotationColor,
            opacity: annotationOpacity,
            modalities: Object.keys(annotationsProvider.annotationModalities).filter(index => annotationsProvider.annotationModalities[index]['checked'] === true).map(modality => annotationsProvider.annotationModalities[modality]['label']),
            tags: annotationTags,
            loaded: true,
            group_id: selectedAnnotation.group_id,
            user_id: selectedAnnotation.user_id
        }
        // On a failed save, skip close(): the caller closes only the confirmation
        // prompt, leaving this modal open with the error.
        if (props.isHotkeysModal) { // handles Save actions for keyboard shortcuts
            // Ignore repeat clicks while a save is in flight
            if (!props.onSave || saveShortcutTask.isWaiting) {
                return
            }
            data.triggers = hotkeyConfig?.triggers
            if (!await runWithFooterError(saveShortcutTask, props.mode === "CREATE" ? "Could not create shortcut. Please try again." : "Could not save shortcut. Please try again.", data).then(() => true, () => false)) {
                return
            }
            await close()
        } else { // handles Save actions for annotations
            // Ignore repeat clicks while a save is in flight
            if (saveAnnotationTask.isWaiting) {
                return
            }
            if (!await handleModifyExistingAnnotation(data)) {
                return
            }
            props.onSave?.(data)
            await close()
            annotationsProvider.resetAnnotationsProvider()
        }
    }

    /**
    * Handles saving changes to an annotation configuration, saving changes to a keyboard shortcut's configurations, or saving changes after creating a new keyboard shortcut
    * 
    * @param {Dictionary} data - The new configurations including all changes made to the selected annotation
    */
    const handleModifyExistingAnnotation = (data) => {
        if (!canModifyAnnotation) {
            return
        }

        return runWithFooterError(saveAnnotationTask, "Could not save annotation. Please try again.", data)
            .then(() => {
                setUneditedAnnotationConfigs(data)
                annotationsProvider.setUneditedModalitiesProvider(annotationsProvider.annotationModalities)
                setUnsavedEdits(false)
                annotationsProvider.setUnsavedModalityEdits(false)
                return true
            })
            .catch(() => false)
    }

    const schema = yup.object({
        tags: yup.array()
            .of(
                yup.string()
                    .trim()
                    .max(25, 'Each tag has a 25 character limit.')
                    .test(
                        'is-unique',
                        'Tag already exists',
                        (value) => !annotationTags.includes(value)
                    )
            )
    })

    const {
        control,
        handleSubmit,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { tags: [] },
        mode: "onChange",
    })

    const [assignShortcutWarning, setAssignShortcutWarning] = useState('')

    const handleChangeKeyboardShortcut = useCallback((event, config) => {
        const excludedKeyCodes = [
            // Function keys (F1 - F12)
            112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123,

            // Navigation keys
            33, 34, 35, 36,

            // Modifiers
            16, 17, 18, 20, 91, 92, 93,

            // Lock keys
            144, 145,

            // Other control keys
            45, 46, 19, 9, 8, 13, 27,

            // Space bar
            32
        ]
        if (!config) {
            return
        }
        let newTrigger = event.key
        let newTriggerCode = event.keyCode
        const usedKeys = props.hotkeyConfigs?.map(config => config.triggers).flat(1).filter(key => key !== null)

        if (config.triggers[0] === newTrigger) {
            setHotkeyConfig(prev => { return { ...prev, isInEditMode: false, editModeWarning: false } })

            return
        } else if (usedKeys.includes(newTrigger) || newTrigger === "ArrowRight" || newTrigger === "ArrowLeft") {
            setAssignShortcutWarning(`'${newTrigger}' shortcut already in use`)
            setHotkeyConfig(prev => { return { ...prev, editModeWarning: true } })

            return
        } else if (excludedKeyCodes.includes(newTriggerCode)) {
            if (newTriggerCode === 32) {
                setAssignShortcutWarning("Space key can't be used")
            } else {
                setAssignShortcutWarning(`${newTrigger} key can't be used`)
            }
            setHotkeyConfig(prev => { return { ...prev, editModeWarning: true } })
            return false
        } else {
            setUnsavedEdits(true)
            setHotkeyConfig(prev => {
                updateHotkeyTrigger(prev, "Primary", newTrigger)
                return { ...prev, isInEditMode: false, editModeWarning: false }
            })
            return
        }
    }, [props.hotkeyConfigs])

    useEffect(() => {
        window.addEventListener('keydown', (event) => {
            if (hotkeyConfig?.isInEditMode) {
                handleChangeKeyboardShortcut(event, hotkeyConfig)
            }
        }, { once: true })
    }, [hotkeyConfig, handleChangeKeyboardShortcut])

    function handleChangeKeyboardShortcutEditMode() {
        setHotkeyConfig(prev => { return { ...prev, isInEditMode: true } })
    }

    function getPrettyHotkeyTrigger(config) {
        if (!config?.triggers || config.triggers[0] === null) {
            return null
        }

        const trigger = config.triggers[0].toLowerCase()

        switch (trigger) {
            case "arrowright": return <MdIcons.MdArrowRightAlt />
            case "arrowleft": return <MdIcons.MdArrowRightAlt style={{ transform: "rotate(180deg)" }} />
            case "arrowdown": return <MdIcons.MdArrowRightAlt style={{ transform: "rotate(90deg)" }} />
            case "arrowup": return <MdIcons.MdArrowRightAlt style={{ transform: "rotate(270deg)" }} />
            case "escape": return "Esc"
            case " ": return "spacebar"
            default: return trigger
        }
    }

    const colorPickerRef = useRef(null)
    const colorPickerBlockRef = useRef(null)

    function toggleColorPicker(event) {
        const node = colorPickerRef.current

        if (node?.style.display === "block") {
            node.style.display = "none"
            return
        }

        if (node) {
            node.style.display = "block"
            const iconRect = event.target.getBoundingClientRect()
            node.style.right = `${iconRect.width / 2}px`
        }
    }

    useEffect(() => {
        const clickHandler = (event) => {
            let clickedOutside = true

            if (colorPickerBlockRef.current?.contains(event.target)) {
                clickedOutside = false
            }

            if (colorPickerRef.current?.contains(event.target)) {
                clickedOutside = false
            }

            if (clickedOutside && colorPickerRef.current) {
                colorPickerRef.current.style.display = "none"
            }
        }

        document.addEventListener("click", clickHandler)
        return () => document.removeEventListener("click", clickHandler)
    })

    return (
        <EditAnnotationContainer>
            <ModalHeader headerText={props.isHotkeysModal ? props.mode === "CREATE" ? "Create Shortcut" : "Edit Shortcut" : canModifyAnnotation ? "Edit Annotation" : "View Annotation"}
                closeFxn={() => {
                    if (!canModifyAnnotation || ((!unsavedEdits) && (annotationTags.slice().sort().join('') === uneditedAnnotationConfigs.tags.slice().sort().join('')))) {
                        if (props.isHotkeysModal) {
                            close()
                            annotationsProvider.resetAnnotationsProvider()
                        } else {
                            close()
                            annotationsProvider.resetAnnotationsProvider()
                        }
                    } else { renderCancelConfirmationModal() }
                }}
            />

            <EditAnnotationContentDiv>
                <div style={{ display: "flex", width: "706px", alignItems: "center" }}>
                    <AnnotationTitleInput type="text" value={annotationTitle} onChange={(e) => { if (canModifyAnnotation) setAnnotationTitle(e.target.value) }} onKeyDown={saveNewAnnotationTitle} maxLength={55} ref={annotationTitleInputRef} disabled={!canModifyAnnotation || hotkeyEditDisabled} />

                    <div style={{ position: "relative", height: "fit-content", width: "fit-content" }}>
                        <div ref={colorPickerBlockRef} style={{ display: "block", cursor: canModifyAnnotation ? "pointer" : "default", marginLeft: "12px", marginRight: '12px', height: "fit-content", opacity: canModifyAnnotation ? 1 : 0.6 }} onClick={(e) => { if (canModifyAnnotation) toggleColorPicker(e) }}>
                            <MdIcons.MdOutlineFormatColorFill size={28} style={{ color: "var(--text-secondary-color)", marginTop: "4px" }} />

                            <div id='ColorLine' style={{ height: "6px", width: "28px", backgroundColor: annotationColor.hex || annotationColor, marginTop: "-6px", position: "relative" }} />
                        </div>

                        <div id={`ColorPickerWindow`} ref={colorPickerRef} style={{ display: 'none', zIndex: 1, position: 'absolute' }}>
                            <TraceColorPicker
                                selectedColor={annotationColor}
                                onChange={handleColorChange}
                                onClose={() => { if (colorPickerRef.current) colorPickerRef.current.style.display = "none" }}
                            />
                        </div>

                    </div>


                    {canModifyAnnotation && (
                        <MobergButton
                            theme={MobergTheme.RED}
                            variant={MobergButtonVariant.FILLED}
                            shape={MobergButtonShape.SQUARE}
                            buttonStyle={{ height: "40px", width: "40px", display: props.mode === "CREATE" ? "none" : "block" }}
                            onClick={renderDeleteAnnotationModal}
                            dataTestId="edit-annotation-delete-button"
                        >
                            <MdDelete size={MobergIconSize.REGULAR} />
                        </MobergButton>
                    )}
                </div>

                <div style={{ display: props.isHotkeysModal ? 'flex' : 'none', padding: '10px 0px 0px', alignItems: 'center' }}>
                    <MdIcons.MdOutlineKeyboard
                        size={24}
                        style={{ color: "var(--color-text-primary)", marginRight: "4px" }}
                        onClick={() => { }} />
                    <h1 style={{ margin: "0px", fontFamily: MobergFontFamily.REGULAR, fontWeight: 600, fontSize: '16px', color: 'var(--color-text-primary)', marginRight: '10px' }}>Assigned Key</h1>

                    <KeyboardButton style={{ color: "var(--color-accent-interactive)", display: hotkeyConfig?.isInEditMode ? "none" : "block", background: "var(--color-hover-neutral)", cursor: "pointer", marginRight: '3px' }} onClick={() => { handleChangeKeyboardShortcutEditMode(hotkeyConfig) }}>
                        {getPrettyHotkeyTrigger(hotkeyConfig)}
                    </KeyboardButton>

                    <AssignShortcutMessage
                        placeholder="Press key to assign shortcut"
                        style={{ display: hotkeyConfig?.editModeWarning ? "none" : hotkeyConfig?.isInEditMode ? "block" : "none" }}
                    >
                        Press key to assign shortcut
                    </AssignShortcutMessage>
                    <AssignShortcutWarning
                        style={{ display: hotkeyConfig?.editModeWarning ? "block" : "none" }}
                        onClick={() => { handleChangeKeyboardShortcutEditMode(hotkeyConfig) }}>
                        {assignShortcutWarning}
                    </AssignShortcutWarning>
                </div>

                <div style={{ width: "420px", marginTop: "6px", display: props.isHotkeysModal ? "none" : "flex", height: "50px" }}>
                    <div style={{ display: "flex", marginRight: '20px' }}>
                        <MdIcons.MdAccountCircle size={18} style={{ color: "var(--color-text-primary)", marginTop: "3px", marginRight: "4px" }} />
                        <h1 style={{ fontSize: "16px", fontWeight: "600", fontFamily: MobergFontFamily.REGULAR }}>Annotator</h1>
                    </div>

                    <div>
                        <div style={{ display: "flex" }}>
                            <p style={{ whiteSpace: "nowrap" }}>{annotatorName}</p>
                            <p style={{ color: "var(--text-secondary-color)", textDecorationLine: "underline", marginLeft: "8px" }}>{annotatorEmail}</p>
                        </div>
                        <p style={{ marginTop: "-18px" }}>{annotationTimestamp}</p>
                    </div>
                </div>

                <div style={{ width: "705px", height: "145px", display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                    <AnnotationDescriptionTextInput
                        style={{ marginTop: props.isHotkeysModal ? "16px" : "0px", border: annotationDescription.length > 500 ? "1px solid #E54E58" : "1px solid var(--color-border-default)" }}
                        value={annotationDescription}
                        onChange={(e) => { if (canModifyAnnotation) setAnnotationDescription(e.target.value) }}
                        placeholder={props.isHotkeysModal ? "Write shortcut description here" : "Write annotation description here"}
                        onFocus={(e) => {
                            if (annotationDescription.length > 500) {
                                e.target.style.border = '1px solid #E54E58'
                            } else {
                                e.target.style.border = `1px solid var(--color-accent-interactive)`
                            }
                        }}
                        onBlur={(e) => {
                            // Remove styling when the input loses focus
                            if (annotationDescription.length > 500) {
                                e.target.style.border = '1px solid #E54E58'
                            } else {
                                e.target.style.border = '1px solid var(--color-border-default)'
                            }
                        }}
                        maxLength={600}
                        disabled={!canModifyAnnotation || hotkeyEditDisabled}
                    />

                    <div id="descriptionCharacterCount" style={{ fontSize: "14px", fontFamily: MobergFontFamily.REGULAR, color: annotationDescription.length > 500 ? "#E54E58" : "var(--color-text-primary)", width: "fit-content", height: "fit-content", alignSelf: 'end' }}>
                        {annotationDescription.length} / 500 characters
                    </div>

                </div>

                <TagsContentDiv>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <MdIcons.MdSell size={15} style={{ color: "var(--color-text-primary)", marginRight: "4px" }} />
                        <h1 style={{ margin: "0px" }}>Tags</h1>
                    </div>
                    <MultivalueTextField
                        inputId="annotation-tags-id"
                        label=""
                        tooltip=""
                        placeholder="+ add more tags"
                        autoFocus={false}
                        control={control}
                        name="tags"
                        numberColumn={5}
                        required={false}
                        setterFunction={addTag}
                        deleteFunction={removeTag}
                        preselect={annotationTags}
                        errors={tagError}
                        styles={{ background: !canModifyAnnotation || hotkeyEditDisabled ? 'var(--color-hover-neutral)' : 'var(--color-surface-panel)', width: '705px', maxHeight: '100px', overflow: 'auto', marginTop: '0px' }}
                        disabled={!canModifyAnnotation || hotkeyEditDisabled}
                    />
                    <div id="tagsCount" style={{ fontSize: "14px", fontFamily: MobergFontFamily.REGULAR, color: annotationTags.length > 10 ? "#E54E58" : "var(--color-text-primary)", width: "fit-content", height: "fit-content", marginTop: '4px', marginLeft: 'auto' }}>
                        {`${annotationTags.length} / 10 tags`}
                    </div>
                </TagsContentDiv>

                {!props.isHotkeysModal ? (<>
                    <AnnotationTypeContainer style={{ display: props.isHotkeysModal ? 'none' : 'block' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <h1 style={{ margin: "0px", fontFamily: 'Source Sans Pro', fontWeight: 600, fontSize: '16px' }}>
                                Annotation Type
                            </h1>
                        </div>
                        <div style={{ display: "flex", gap: "20px" }}>
                            <CircularCheckboxComponent
                                style={{ color: annotationsProvider.isPointAnnotation ? "var(--color-accent-interactive)" : "var(--color-border-default)" }}
                                checked={annotationsProvider.isPointAnnotation}
                                onChangeFxn={() => { if (canModifyAnnotation) annotationsProvider.setIsPointAnnotation(!annotationsProvider.isPointAnnotation) }}
                                checkboxLabel={"Point Annotation"}
                            />
                            <CircularCheckboxComponent
                                style={{ color: !annotationsProvider.isPointAnnotation ? "var(--color-accent-interactive)" : "var(--color-border-default)" }}
                                checked={!annotationsProvider.isPointAnnotation}
                                onChangeFxn={() => { if (canModifyAnnotation) annotationsProvider.setIsPointAnnotation(!annotationsProvider.isPointAnnotation) }}
                                checkboxLabel={"Span Annotation"}
                            />
                        </div>
                    </AnnotationTypeContainer>

                    <AnnotationIntervalContainer style={{ display: props.isHotkeysModal ? 'none' : 'block' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <h1 style={{ fontFamily: 'Source Sans Pro', fontWeight: 600, fontSize: '16px' }}>
                                Annotation Interval
                            </h1>
                        </div>
                        <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                                <div style={{ flex: 1 }}>
                                    <FieldLabel>Start Time</FieldLabel>
                                    <DateTimeField
                                        value={dayjs(startDate).tz(timeZone)}
                                        onChange={(newValue) => {
                                            if (!canModifyAnnotation) {
                                                return
                                            }
                                            if (newValue) {
                                                setStartDate(newValue.valueOf())
                                                setUnsavedEdits(true)
                                                setSaveError("")
                                            }
                                        }}
                                        format="MMM D, YYYY, h:mm:ss A"
                                        minDateTime={dayjs(fileStartDate).tz(timeZone)}
                                        maxDateTime={annotationsProvider.isPointAnnotation
                                            ? dayjs(fileEndDate).tz(timeZone)
                                            : dayjs(endDate).tz(timeZone)}
                                        fullWidth
                                        size="small"
                                        disabled={!canModifyAnnotation}
                                        onError={(err) => { setStartIntervalError(err); setSaveError("") }}
                                    />
                                    <FieldHint>
                                        File Start: {formatDate(fileStartDate, dateFormatOpts)}
                                    </FieldHint>
                                    {startErrorMessage && <FieldError>{startErrorMessage}</FieldError>}
                                </div>

                                {!annotationsProvider.isPointAnnotation && (
                                    <div style={{ flex: 1 }}>
                                        <FieldLabel>End Time</FieldLabel>
                                        <DateTimeField
                                            value={dayjs(endDate).tz(timeZone)}
                                            onChange={(newValue) => {
                                                if (!canModifyAnnotation) {
                                                    return
                                                }
                                                if (newValue) {
                                                    setEndDate(newValue.valueOf())
                                                    setUnsavedEdits(true)
                                                    setSaveError("")
                                                }
                                            }}
                                            format="MMM D, YYYY, h:mm:ss A"
                                            minDateTime={dayjs(startDate).tz(timeZone)}
                                            maxDateTime={dayjs(fileEndDate).tz(timeZone)}
                                            fullWidth
                                            size="small"
                                            disabled={!canModifyAnnotation}
                                            onError={(err) => { setEndIntervalError(err); setSaveError("") }}
                                        />
                                        <FieldHint>
                                            File End: {formatDate(fileEndDate, dateFormatOpts)}
                                        </FieldHint>
                                        {endErrorMessage && <FieldError>{endErrorMessage}</FieldError>}
                                    </div>
                                )}

                                <MobergButton
                                    variant={MobergButtonVariant.CLEAR}
                                    onClick={() => {
                                        if (!canModifyAnnotation) {
                                            return
                                        }
                                        setStartDate(uneditedAnnotationConfigs.start_time)
                                        setEndDate(uneditedAnnotationConfigs.end_time)
                                    }}
                                    disabled={!canModifyAnnotation ||
                                             (startDate === uneditedAnnotationConfigs.start_time &&
                                             endDate === uneditedAnnotationConfigs.end_time)}
                                    style={{ padding: "2px 10px", height: "auto", marginTop: "26px" }}
                                >
                                    Reset Changes
                                </MobergButton>
                            </div>
                            {saveError && <FieldError>{saveError}</FieldError>}
                        </LocalizationProvider>
                    </AnnotationIntervalContainer>
                </>) : null}

                <div style={{ textAlign: "center", marginBottom: "12px" }}>
                    <h1 style={{ fontFamily: 'Source Sans Pro', fontWeight: 600, fontSize: '16px' }}>
                        Modalities
                    </h1>
                </div>

                <ModalitiesContentContainer style={{ overflowY: "scroll", opacity: canModifyAnnotation ? 1 : 0.75 }}>
                    <EditAnnotationModalities 
                        config={hotkeyConfig ? hotkeyConfig : null} 
                        mode={props.mode ? props.mode : null} 
                        isHotkeysModal={props.isHotkeysModal} 
                        disabled={!canModifyAnnotation}
                    />
                </ModalitiesContentContainer>

            </EditAnnotationContentDiv>

            <FooterWrapper>
                <hr style={{ border: "1px solid var(--color-border-default)", width: "100%", marginBottom: "0px" }} />

                <ModalFooter style={{ display: "flex", flexDirection: "row-reverse" }}>
                    {canModifyAnnotation && (
                        <ModalFooterButton
                            buttonText="Save"
                            color=""
                            buttonStyle={{ width: "77px", flex: "0", padding: "8px 32px" }}
                            onClickFxn={() => { handleSubmit(handleSaveWithConfirmation()) }}
                            disabled={(props.isHotkeysModal ? saveShortcutTask.isWaiting : saveAnnotationTask.isWaiting) || !unsavedEdits || hotkeyEditDisabled || annotationTags.length > 10 || annotationDescription.length > 500 || annotationTitle.length === 0 || startIntervalError || (!annotationsProvider.isPointAnnotation && endIntervalError)}
                        />
                    )}
                    {footerError && (
                        <FieldError style={footerErrorStyle}>{footerError}</FieldError>
                    )}
                </ModalFooter>
            </FooterWrapper>
        </EditAnnotationContainer>
    )
}

const EditAnnotationContainer = styled.div`
    width: 766px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const EditAnnotationContentDiv = styled.div`
    padding-left: 30px;
    padding-right: 30px;
    display: flex;
    position: relative;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: var(--color-scrollbar-thumb) transparent;
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: var(--color-scrollbar-thumb);
        border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary-color);
    }
    h1 {
        font-family: 'Source Sans Pro';
        font-style: normal;
        font-weight: 700;
        font-size: 21px;
        line-height: 150%;
        color: var(--color-text-primary);
    }
    p {
        font-family: 'Source Sans Pro';
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        color: var(--color-text-primary);
    }
`;

const AnnotationTitleInput = styled.input`
    width: 610px;
    height: 40px;
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    padding-left: 4px;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 700;
    font-size: 21px;
    color: var(--color-text-primary);
    background: var(--color-input-bg);
    line-height: 150%;
    flex: 1;
    &: focus {
        border: 1px solid var(--color-accent-interactive);
    }
`;

const AnnotationDescriptionTextInput = styled.textarea`
    box-sizing: border-box;
    width: 705px;
    height: 125px;
    background: var(--color-input-bg);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    padding: 12px 12px;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 120%;
    resize: none;
    &:focus {
        outline: none;
        border-color: var(--color-accent-interactive);
    }
    &:disabled {
        background: var(--color-hover-neutral);
    }
    &::placeholder {
        color: var(--text-secondary-color);
    }
`;

const TagsContentDiv = styled.div`
    width: 706px;
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    h1 {
        font-family: 'Source Sans Pro';
        font-weight: 600;
        font-size: 16px;
        color: var(--color-text-primary);
        line-height: 150%;
    }
`;

const ModalitiesContentContainer = styled.div`
    width: 704px;
    min-height: 310px;
    max-height: 310px;
    border-radius: 6px;
    border: 1px solid var(--color-border-default);
    padding: 0px 9px 7px 9px;
    flex-wrap: wrap;
    flex-shrink: 0;
    h2 {
        font-family: 'Source Sans Pro';
        font-size: 16px;
        font-weight: 600;
        font-style: normal;
        line-height: 150%;
        color: var(--text-secondary-color);
    }
`;

const AnnotationTypeContainer = styled.div`
    width: 705px;
    margin-bottom: 16px;
    h1 {
        font-family: 'Source Sans Pro';
        font-weight: 600;
        font-size: 16px;
        color: var(--color-text-primary);
    }
`;

const AnnotationIntervalContainer = styled.div`
    width: 705px;
    margin-bottom: 24px;
    h1 {
        font-family: 'Source Sans Pro';
        font-weight: 600;
        font-size: 16px;
        color: var(--color-text-primary);
    }
`;

const KeyboardButton = styled.div`
    height: 24px;
    width: fit-content;
    background: var(--color-hover-neutral);
    padding: 1.5px 10px;
    border-radius: 4px;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    align-items: center;
    text-align: center;
    text-transform: lowercase;
    color: var(--text-secondary-color);
    margin-right: 3px;
`;

const AssignShortcutMessage = styled.text`
    background: none;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 150%;
    color: var(--color-accent-interactive);
    text-align: right;
    width: fit-content;
    white-space: nowrap;
    border: none;
    margin-right: 3px;
    &::placeholder {
        font-family: 'Source Sans Pro';
        font-style: normal;
        font-weight: 600;
        font-size: 14px;
        line-height: 150%;
        color: var(--color-accent-interactive);
    }
`;

const AssignShortcutWarning = styled.div`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 150%;
    color: var(--text-secondary-color);
    width: fit-content;
    cursor: pointer;
    margin-right: 3px;
`;

const FooterWrapper = styled.div`
    flex-shrink: 0;
    background: var(--color-surface-panel);
`;
